/**
 * Supabase client singleton.
 *
 * Reads URL + publishable key from Vite env using the SB_ naming as the
 * primary source; falls back to the legacy SUPABASE_ names for compatibility
 * with older snippets and SDKs. The service_role / sb_secret key is never
 * imported here — it is referenced only inside Supabase Edge Functions via
 * Deno.env. RLS is the sole authorization layer for client-side queries.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { AppError } from './errors/AppError'

const env = import.meta.env

const url =
  env.VITE_SB_URL ??
  env.VITE_SUPABASE_URL ??
  ''

const publishableKey =
  env.VITE_SB_PUBLISHABLE_KEY ??
  env.VITE_SB_ANON_KEY ??
  env.VITE_SUPABASE_ANON_KEY ??
  ''

if (!url || !publishableKey) {
  throw new AppError({
    code:        'MISSING_SB_ENV',
    message:     'VITE_SB_URL and VITE_SB_PUBLISHABLE_KEY must be set (or their VITE_SUPABASE_* fallbacks)',
    userMessage: 'Application is not configured. Contact your administrator.',
    status:      500,
  })
}

export const supabase: SupabaseClient = createClient(url, publishableKey, {
  auth: {
    persistSession:     true,
    autoRefreshToken:   true,
    detectSessionInUrl: true,
  },
})

/** Public: the project URL the client is bound to (used by photo upload confirm). */
export const SB_URL = url
